import React, { useEffect, useRef, useState } from "react";

/**
 * CustomCursor
 * - Dot kecil: mengikuti mouse dengan presisi tinggi (no lag)
 * - Ring besar: mengikuti dengan smooth lag via lerp
 * - State: default | hover (link/button) | image (gambar)
 * - Otomatis hide native cursor via CSS di global
 */
const CustomCursor = () => {
  const dotRef = useRef(null);
  const ringRef = useRef(null);

  // Posisi target (mouse)
  const mouse = useRef({ x: -100, y: -100 });
  // Posisi ring saat ini (lerp)
  const ring = useRef({ x: -100, y: -100 });

  const [state, setState] = useState("default"); // "default" | "hover" | "image" | "text"
  const [visible, setVisible] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    // Hide native cursor
    document.documentElement.style.cursor = "none";
    document.body.style.cursor = "none";

    const onMove = (e) => {
      mouse.current = { x: e.clientX, y: e.clientY };
      if (!visible) setVisible(true);

      // Dot: posisi langsung (presisi)
      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      }
    };

    const onLeave = () => setVisible(false);
    const onEnter = () => setVisible(true);

    // Delegated hover detection
    const onOver = (e) => {
      const el = e.target;
      if (
        el.closest("a") ||
        el.closest("button") ||
        el.tagName === "BUTTON" ||
        el.tagName === "A"
      ) {
        setState("hover");
      } else if (el.tagName === "IMG" || el.closest("[data-cursor='image']")) {
        setState("image");
      } else if (
        el.tagName === "P" ||
        el.tagName === "H1" ||
        el.tagName === "H2" ||
        el.tagName === "H3" ||
        el.tagName === "SPAN"
      ) {
        setState("text");
      } else {
        setState("default");
      }
    };

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);
    document.addEventListener("mouseenter", onEnter);
    document.addEventListener("mouseover", onOver, { passive: true });

    // Lerp loop untuk ring
    const lerp = (a, b, t) => a + (b - a) * t;

    const tick = () => {
      ring.current.x = lerp(ring.current.x, mouse.current.x, 0.12);
      ring.current.y = lerp(ring.current.y, mouse.current.y, 0.12);

      if (ringRef.current) {
        ringRef.current.style.transform = `translate(${ring.current.x}px, ${ring.current.y}px)`;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("mouseenter", onEnter);
      document.removeEventListener("mouseover", onOver);
      cancelAnimationFrame(rafRef.current);
      document.documentElement.style.cursor = "";
      document.body.style.cursor = "";
    };
  }, [visible]);

  // Konfigurasi per state
  const config = {
    default: {
      dot: "w-1.5 h-1.5 bg-white",
      ring: "w-8 h-8 border border-white/40",
      ringExtra: "",
    },
    hover: {
      dot: "w-1 h-1 bg-white",
      ring: "w-12 h-12 border border-white bg-white/5",
      ringExtra: "scale-110",
    },
    image: {
      dot: "w-1 h-1 bg-white opacity-0",
      ring: "w-16 h-16 border border-white/60 bg-black/20 backdrop-blur-sm",
      ringExtra: "",
    },
    text: {
      dot: "w-0.5 h-5 bg-white rounded-none",
      ring: "w-0 h-0 border-0 opacity-0",
      ringExtra: "",
    },
  };

  const c = config[state] || config.default;

  if (typeof window !== "undefined" && window.matchMedia("(pointer: coarse)").matches) {
    // Touch device — jangan render cursor
    return null;
  }

  return (
    <>
      {/* Ring — trailing */}
      <div
        ref={ringRef}
        aria-hidden="true"
        className={`
          pointer-events-none fixed top-0 left-0 z-[9999]
          -translate-x-1/2 -translate-y-1/2
          rounded-full
          transition-[width,height,background-color,border-color,opacity] duration-200 ease-out
          ${c.ring} ${c.ringExtra}
          ${visible ? "opacity-100" : "opacity-0"}
        `}
        style={{ willChange: "transform" }}
      />

      {/* Dot — presisi */}
      <div
        ref={dotRef}
        aria-hidden="true"
        className={`
          pointer-events-none fixed top-0 left-0 z-[9999]
          -translate-x-1/2 -translate-y-1/2
          rounded-full
          transition-[width,height,background-color,border-radius,opacity] duration-150 ease-out
          ${c.dot}
          ${visible ? "opacity-100" : "opacity-0"}
        `}
        style={{ willChange: "transform" }}
      />
    </>
  );
};

export default CustomCursor;
